import React from 'react';
import Typography from '@lib/components/Typography/Typography';
import Button, {
  ButtonSizes,
  ButtonTypes,
} from '@lib/components/Button/Button';
import Chip, { ChipTypes, ChipSizes } from '@lib/components/Chip/Chip';
import IconButton, {
  IconButtonTypes,
} from '@lib/components/IconButton/IconButton';
import IconSurface from '@lib/components/IconSurface/IconSurface';
import Tooltip from '@lib/components/Tooltip/Tooltip';
import { Colors } from '@lib/enums/colors';
import styles from './SecurityCard.module.scss';

export interface SecurityCardProps {
  icon: string;
  iconColor?: Colors;
  title: string;
  subtitle: React.ReactNode;
  buttonText: string;
  onButtonClick: () => void;
  isActive?: boolean;
  activeLabel?: string;
  /**
   * Optional callback for delete action.
   * When provided, a delete icon button will be shown next to the main button.
   */
  onDeleteClick?: () => void;
  /**
   * Whether the button is in loading state
   */
  isLoading?: boolean;
  disabled?: boolean;
  tooltipText?: string;
}

function SecurityCard({
  icon,
  iconColor = Colors.Blue,
  title,
  subtitle,
  buttonText,
  onButtonClick,
  isActive,
  activeLabel,
  onDeleteClick,
  isLoading,
  disabled,
  tooltipText,
}: SecurityCardProps) {
  const isDisabledWithTooltip = !!(tooltipText && disabled);

  const mainButton = (
    <Button
      buttonText={buttonText}
      buttonType={ButtonTypes.primaryOutlined}
      buttonSize={ButtonSizes.small}
      onClick={onButtonClick}
      isLoading={isLoading}
      disabled={isLoading || disabled}
    />
  );

  const deleteButton = onDeleteClick ? (
    <IconButton
      icon="delete"
      onClick={onDeleteClick}
      type={IconButtonTypes.QuaternaryFilled}
      symbolsOutlined
      rounded
      disabled={disabled}
    />
  ) : null;

  return (
    <div className={styles.card}>
      <IconSurface
        icon={icon}
        color={iconColor}
        symbolsOutlined
        surfaceClassName={styles.cardIconCircle}
        iconClassName={styles.cardIcon}
      />
      <div className={styles.cardContent}>
        <div className={styles.cardInfo}>
          <div className={styles.cardTitleRow}>
            <Typography variant="body" strong>
              {title}
            </Typography>
            {isActive && activeLabel && (
              <Chip
                type={ChipTypes.chipGreen}
                label={activeLabel}
                size={ChipSizes.small}
              />
            )}
          </div>
          {!!subtitle && (
            <Typography variant="caption" className={styles.cardSubtitle}>
              {subtitle}
            </Typography>
          )}
        </div>
        <div className={styles.cardActions}>
          {isDisabledWithTooltip ? (
            <Tooltip body={tooltipText}>{mainButton}</Tooltip>
          ) : (
            mainButton
          )}
          {deleteButton &&
            (isDisabledWithTooltip ? (
              <Tooltip body={tooltipText}>{deleteButton}</Tooltip>
            ) : (
              deleteButton
            ))}
        </div>
      </div>
    </div>
  );
}

export default SecurityCard;
